<script setup lang="ts">
import { ref, computed, watch, useIntervalFn, onClickOutside } from '#imports'

export interface CheeseTickerProps {
    shown: boolean
    zIndex?: string | number
    position?: 'top' | 'bottom'
    showDetails?: boolean
    showProgress?: boolean
    displayTimeInSeconds?: number
    topPosition?: number
    bottomPosition?: number
}

const props = withDefaults(defineProps<CheeseTickerProps>(), {
    showDetails: false,
    showProgress: false,
    position: 'top',
    displayTimeInSeconds: 3,
    topPosition: 30,
    bottomPosition: 30,
})

const emit = defineEmits([
    'show-details',
    'close',
])

const progress = ref(0)
const cheeseTicker = ref(null)

const { pause, resume } = useIntervalFn(() => {
    if (progress.value < 100) {
        progress.value += 100 / (props.displayTimeInSeconds * 10)

        return
    }

    pause()
    close()
}, 100, { immediate: false })

// The display time counts from showing, not mounting: a deposit receipt mounts before its data has loaded
watch(() => props.shown, (shown) => {
    if (!shown) {
        pause()

        return
    }

    resume()
}, { immediate: true })

function close() {
    progress.value = 101
    emit('close')
}

const progressWidth = computed(() => `${Math.min(progress.value, 100)}%`)

onClickOutside(cheeseTicker, () => close(), {
    capture: false,
})
</script>

<template>
    <transition :name="props.position === 'bottom' ? 'cheese-ticker-bottom' : 'cheese-ticker-top'" appear>
        <div
            v-if="shown && progress <= 100"
            ref="cheeseTicker"
            :class="[`cheese-ticker cheese-ticker--${props.position}`, { 'cheese-ticker--details': $slots.content && props.showDetails }]"
            :style="{ zIndex: props.zIndex }"
            data-test="cheese-ticker"
            @mouseover="pause"
            @mouseleave="resume"
        >
            <div class="cheese-ticker__header" @click="emit('show-details', !props.showDetails)">
                <slot />

                <UIIcon v-if="$slots.content" name="arrow-down" size="16" />
            </div>

            <div v-if="$slots.content" class="cheese-ticker__content-wrapper">
                <div class="cheese-ticker__content">
                    <slot name="content" />
                </div>
            </div>

            <div v-if="props.showProgress" class="cheese-ticker__progress" />
        </div>
    </transition>
</template>

<style lang="scss" scoped>
.cheese-ticker {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100vw - 40px);
    max-width: 350px;
    border-radius: var(--border-radius, $border-radius);
    color: $white; // Static value, same color on all sites
    background: #363636; // Static value, same color on all sites
    overflow: hidden;
    will-change: top, bottom, opacity;

    &__progress {
        position: absolute;
        bottom: 0;
        left: 0;
        width: v-bind(progressWidth);
        background: $green;
        height: 5px;
        transition: width 0.1s linear;
    }

    &__header {
        display: flex;
        align-items: center;
        font-size: $font-size-sm;
        gap: 7px;
        padding: 10px;
        cursor: pointer;

        .icon--arrow-down {
            margin-left: auto;
            color: $white;
            transition: transform 0.3s ease;
        }
    }

    &__content {
        display: inline-block;
        font-size: $font-size-xs;
        border-top: 1px solid #e0e0e080;
        border-bottom: 1px solid #e0e0e080;
        padding: 10px;
        margin: 0 10px 10px;
        width: calc(100% - 20px);
        opacity: 0;
        transition: all 0.4s ease 0.2s;

        &-wrapper {
            transition: all 0.4s ease-out;
            max-height: 0;
            overflow: hidden;
        }
    }

    &--details {
        .cheese-ticker {
            &__content {
                opacity: 1;

                &-wrapper {
                    max-height: 200px;
                }
            }
        }

        .icon--arrow-down {
            transform: rotate(-180deg);
        }
    }

    &--top {
        top: v-bind('props.topPosition + "px"');
    }

    &--bottom {
        bottom: v-bind('props.bottomPosition + "px"');
    }
}

// TOP TRANSITION
.cheese-ticker-top-enter-from {
    top: -100px;
    opacity: 0;
}

.cheese-ticker-top-enter-active {
    transition:
        top 0.3s ease,
        opacity 0.3s ease;
}

.cheese-ticker-top-enter-to {
    top: v-bind('props.topPosition + "px"');
    opacity: 1;
}

.cheese-ticker-top-leave-from {
    top: v-bind('props.topPosition + "px"');
    opacity: 1;
}

.cheese-ticker-top-leave-active {
    transition: top 0.3s;
}

.cheese-ticker-top-leave-to {
    top: -100px;
}

// BOTTOM TRANSITION
.cheese-ticker-bottom-enter-from {
    bottom: -100px;
    opacity: 0;
}

.cheese-ticker-bottom-enter-active {
    transition:
        bottom 0.3s ease,
        opacity 0.3s ease;
}

.cheese-ticker-bottom-enter-to {
    bottom: v-bind('props.bottomPosition + "px"');
    opacity: 1;
}

.cheese-ticker-bottom-leave-from {
    bottom: v-bind('props.bottomPosition + "px"');
    opacity: 1;
}

.cheese-ticker-bottom-leave-active {
    transition: bottom 0.3s;
}

.cheese-ticker-bottom-leave-to {
    bottom: -100px;
}
</style>
